import Badge from '@/common/components/Badge';
import Box from '@/common/components/Box';
import Button from '@/common/components/Button';
import { CopyButton } from '@/common/components/CopyButton';
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder';
import RequestNewCardModal from '@/common/components/RequestNewCardModal';
import ResumeBox from '@/common/components/ResumeBox';
import Select from '@/common/components/Select';
import TextField from '@/common/components/TextField';
import { userStatusTranslation } from '@/common/constants/userStatus.constants';
import { useCardWithSecureInformation } from '@/common/hooks/queries/useCardWithSecureInformation';
import { useCopyButton } from '@/common/hooks/useCopyButton';
import { useCurrentUser } from '@/common/hooks/useCurrentUser';
import useUserPermissions from '@/common/hooks/useUserPermissions';
import { dateTransformer } from '@/common/utils/dateTransformer';
import { formatCpf, formatPhoneNumber } from '@/common/utils/formatters';
import { useMineCompany } from '@/features/company/hooks/useMineCompany';
import { cardStatusTranslation } from '@/features/equipments/constants/cardStatusTranslation.constant';
import { CreateClientUserCard } from '@/features/users/components/CreateClientUserCard';
import RequestConsumptionPasswordReset from '@/features/users/components/RequestConsumptionPasswordReset';
import RequestPasswordReset from '@/features/users/components/RequestPasswordReset';
import BlockPersonalCard from '@/features/users/components/showUser/BlockPersonalCard';
import { ConsumptionLimit } from '@/features/users/components/showUser/ConsumptionLimit';
import UnblockPersonalCard from '@/features/users/components/showUser/UnblockPersonalCard';
import { useClientUser } from '@/features/users/hooks/useClientUser';
import { useClientUserRequestNewCard } from '@/features/users/hooks/useClientUserRequestNewCard';
import { useQueryClient } from '@tanstack/react-query';
import { UserRoundIcon } from "lucide-react";
import React from 'react';
import { useParams } from 'react-router-dom';

const GeneralInformationTab = () => {
  const queryClient = useQueryClient();

  const { id: userId } = useParams();

  const { data: userMe } = useCurrentUser({});

  const { data: clientUserData } = useClientUser({ id: userId || '' });

  const { hasUserPermission } = useUserPermissions();

  const { control, copySuccess, copyFailed } = useCopyButton();

  const { data: company } = useMineCompany();

  const {
    mutate: personalCard,
    isPending: personalCardIsPending,
  } = useCardWithSecureInformation({
    onSuccess: (response) => copySuccess(response?.data?.data?.number),
    onError: copyFailed
  });

  const requestNewCardMutation = useClientUserRequestNewCard({
    userId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientUser', userId] });
    },
  });

  const cardToDisplay = clientUserData?.cards.at(-1);

  return (
    <>
      <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto max-h-[calc(100vh-192px)]">
        <p className="text-lg font-bold">Dados pessoais</p>
        <div className="flex flex-col gap-2">
          <p className="text-xs">Foto</p>
          <ImageWithPlaceholder
            url={clientUserData?.profile_picture?.url}
            alt={clientUserData?.full_name}
            className="size-20"
            iconClassName="size-16"
            placeholderIcon={UserRoundIcon}
          />
        </div>
        <div className="flex gap-2 w-full justify-between">
          <TextField
            label="CPF"
            className="w-3/4"
            value={
              clientUserData?.cpf
                ? formatCpf(clientUserData?.cpf)
                : ''
            }
            disabled
          />
          <TextField
            label="Data de nascimento"
            className="w-1/4"
            value={
              clientUserData?.date_of_birth
                ? dateTransformer(clientUserData?.date_of_birth)
                : ''
            }
            disabled
          />
        </div>
        <div className="flex gap-2 w-full justify-between">
          <TextField
            label="Nome"
            className="w-3/4"
            value={clientUserData?.full_name}
            disabled
          />
          <Select
            label="Perfil"
            className="w-1/4"
            value={clientUserData?.user_roles?.map(({ role }) => ({
              label: role.display_name,
              value: role.id,
            }))}
            isMulti
            isDisabled
          />
        </div>
        <TextField
          label="Status"
          value={
            userStatusTranslation[
            (clientUserData?.status ||
              'default') as keyof typeof userStatusTranslation
            ]
          }
          disabled
        />
        <TextField
          label="Matrícula"
          value={clientUserData?.enrollment_number}
          disabled
        />
        <TextField
          label="Telefone"
          value={clientUserData?.phone?.number ? formatPhoneNumber(clientUserData?.phone?.number) : ''}
          disabled
        />
        <TextField
          label="Email"
          value={clientUserData?.email}
          disabled
        />

        <div className="flex gap-2 w-full justify-between">
          <TextField
            disabled
            label="Número da CNH"
            className="grow"
            value={clientUserData?.current_driver_license_registration_number || ''}
          />

          <Select
            label="Categorias da CNH"
            placeholder=""
            className="grow"
            value={clientUserData?.user_driver_license_categories?.map(({ license_category }) => ({
              label: license_category.display_name,
              value: license_category.id,
            }))}
            isMulti
            isDisabled
          />

          <TextField
            disabled
            label="Data de vencimento"
            className="grow"
            value={clientUserData?.current_driver_license_expiration_date ? dateTransformer(clientUserData?.current_driver_license_expiration_date) : ''}
          />
        </div>

        <div className="w-full rounded-sm p-4 bg-white-100">
          {!clientUserData?.cards?.length ? (
            <div className="flex w-full items-center justify-between">
              <p>Cartão personal</p>
              {company?.kind !== "public_company" && (
                <CreateClientUserCard clientUserId={userId} />
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <p>
                    Cartão personal
                  </p>

                  {cardToDisplay?.status === 'blocked' && (
                    <Badge
                      Icon={React.Fragment}
                      text='Bloqueado'
                    />
                  )}
                </div>

                <div className='flex items-center gap-2'>
                  <RequestNewCardModal
                    mutationHook={requestNewCardMutation}
                    operationKlassToCheckPermission='Client::Users::RequestNewCard'
                  />

                  {cardToDisplay?.status === 'blocked' ? (
                    <UnblockPersonalCard userId={userId} cardId={cardToDisplay?.id} />
                  ) : (
                    <BlockPersonalCard userId={userId} cardId={cardToDisplay?.id} />
                  )}
                </div>
              </div>

              <div className='grid grid-cols-2 gap-2'>
                <ResumeBox
                  label='Status do cartão'
                  value={
                    cardToDisplay?.status
                      ? cardStatusTranslation[cardToDisplay?.status as keyof typeof cardStatusTranslation]
                      : '--'
                  }
                />

                <ResumeBox
                  label='Código'
                  value={(
                    <div className='flex items-center gap-2'>
                      <p className='text- font-medium'>
                        {cardToDisplay?.last_four_digits ? `**** **** **** ${cardToDisplay?.last_four_digits}` : ''}
                      </p>

                      <Button
                        variant='link-white'
                        onClick={() => personalCard(cardToDisplay?.id || '')}
                        isLoading={personalCardIsPending}
                      >
                        <CopyButton control={control} className="text-blue-800 size-4" />
                      </Button>
                    </div>
                  )}
                />
              </div>

              <ConsumptionLimit userId={clientUserData?.id} card={cardToDisplay!} />
            </div>
          )}
        </div>

        {clientUserData && userMe?.id !== userId && hasUserPermission('Client::Users::RequestPasswordReset') && !!clientUserData.role_permits_client_user_password_reset_by_others && (
          <div className="w-full rounded-sm p-4 bg-white-100">
            <div className="flex w-full items-center justify-between">
              <p>Senha de acesso ao sistema de gestão TCard</p>

              <RequestPasswordReset id={userId!} />
            </div>
          </div>
        )}

        {clientUserData && hasUserPermission('Client::Users::RequestConsumptionPasswordReset') && (
          <div className="w-full rounded-sm p-4 bg-white-100">
            <div className="flex w-full items-center justify-between">
              <p>Senha para consumo com cartão</p>

              <RequestConsumptionPasswordReset id={userId!} />
            </div>
          </div>
        )}
      </Box>
    </>
  );
};

export default GeneralInformationTab;
